// Navbar.jsx — Mobile menu fully fixed

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiCalendar, FiGlobe, FiArrowRight, FiX } from 'react-icons/fi';
import BookingPopup from './BookingPopup';

const NAV_LINKS = [
  { key: 'experiences', id: 'activities', num: '01' },
  { key: 'food',        id: 'food',       num: '02' },
  { key: 'access',      id: 'access',     num: '03' },
  { key: 'faq',         id: 'faq',        num: '04' },
];

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

/* ── MOBILE LINK ── */
function MobileLink({ label, num, delay, onClick }) {
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, x: -24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className="
        group relative w-full
        flex items-center justify-between text-left
        px-5 py-5 rounded-[20px] overflow-hidden
        bg-white border border-[#e8f0e0]
        shadow-sm active:scale-[.98] transition-transform
      "
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#edf7e5] to-[#fffbe8] opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="relative z-10 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#1f4d2b] text-white flex items-center justify-center text-sm font-black shadow-md flex-shrink-0">
          {num}
        </div>
        <p className="text-[#173617] font-black text-[20px] leading-none">{label}</p>
      </div>
      <span className="relative z-10 text-[#1f4d2b] text-xl font-black">→</span>
    </motion.button>
  );
}

/* ── MAIN ── */
export default function Navbar() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [activeId,  setActiveId]  = useState('');
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map(l => document.getElementById(l.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleLang  = () => i18n.changeLanguage(lang === 'ja' ? 'en' : 'ja');
  const handleNav   = (id) => { scrollTo(id); setMenuOpen(false); };
  const openBooking = () => { setMenuOpen(false); setPopupOpen(true); };

  return (
    <>
      {/* ── NAVBAR ── */}
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[999] transition-all duration-500 px-3 md:px-6 ${scrolled ? 'pt-2' : 'pt-3'}`}
      >
        <div className={`
          max-w-7xl mx-auto rounded-[24px] border
          transition-all duration-500 backdrop-blur-2xl overflow-hidden
          ${scrolled
            ? 'bg-white/98 border-white/60 shadow-2xl'
            : 'bg-white/95 border-white/50 shadow-xl'}
        `}>
          {/* rainbow stripe */}
          <div className="h-[5px] bg-[linear-gradient(90deg,#1f4d2b_0%,#1f4d2b_20%,#7fcf65_20%,#7fcf65_40%,#f0b53a_40%,#f0b53a_60%,#ff8a65_60%,#ff8a65_80%,#3d8c4a_80%,#3d8c4a_100%)]" />

          <div className="h-[70px] px-4 md:px-6 flex items-center justify-between">

            {/* LOGO */}
            <button onClick={() => scrollTo('hero')} className="flex items-center gap-3 shrink-0">
              <div className="rounded-xl overflow-hidden shadow border border-[#d7e5cf] shrink-0">
                <img src="/images/logo.JPG" alt="logo" className="h-[44px] w-auto object-cover" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-[#173617] font-black text-base leading-none">
                  {lang === 'ja' ? '掛川バギーパーク' : 'Kakegawa Buggy'}
                </p>
                <p className="text-[#3d8c4a] text-[10px] font-bold tracking-[0.18em] uppercase mt-1">
                  Outdoor Adventure
                </p>
              </div>
            </button>

            {/* DESKTOP NAV */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map(ln => (
                <button
                  key={ln.key}
                  onClick={() => handleNav(ln.id)}
                  className={`px-4 py-2.5 rounded-xl text-[14px] font-black transition-all duration-200 ${
                    activeId === ln.id
                      ? 'bg-[#1f4d2b] text-white shadow-md'
                      : 'text-[#173617] hover:bg-[#edf7e7]'
                  }`}
                >
                  {t(`nav.${ln.key}`)}
                </button>
              ))}
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-2">
              {/* lang — always visible */}
              <button
                onClick={toggleLang}
                className="flex items-center gap-1.5 h-[38px] sm:h-[44px] px-3 sm:px-4 rounded-xl bg-[#f3f8ef] text-[#1f4d2b] font-black text-xs sm:text-sm border border-[#d9e7d0] hover:bg-[#e3f2d8] transition-all"
              >
                <FiGlobe size={13} />
                {lang === 'ja' ? 'EN' : 'JP'}
              </button>

              {/* phone — md+ */}
              <a
                href="tel:0537269489"
                className="hidden md:flex items-center gap-2 h-[44px] px-4 rounded-xl bg-[#fff5dd] text-[#7a4b00] font-black text-sm border border-[#f3d596] hover:scale-[1.02] transition-all no-underline"
              >
                <FiPhone size={14} />
                0537-26-9489
              </a>

              {/* Book — sm+ */}
              <motion.button
                onClick={openBooking}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                className="hidden sm:flex items-center gap-2 h-[48px] px-5 rounded-xl bg-[#1f4d2b] text-white font-black text-sm shadow-lg hover:bg-[#2d6b38] transition-all"
              >
                <FiCalendar size={15} />
                {lang === 'ja' ? '予約する' : 'Book Now'}
                <FiArrowRight size={14} />
              </motion.button>

              {/* BURGER — below lg */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden w-[48px] h-[48px] rounded-xl bg-[#1f4d2b] flex items-center justify-center shadow-lg"
              >
                <motion.div animate={menuOpen ? 'open' : 'closed'} className="relative w-6 h-6">
                  <motion.span variants={{ closed:{ rotate:0, y:-7 }, open:{ rotate:45, y:0 } }}
                    className="absolute left-0 top-1/2 w-6 h-[2.5px] rounded-full bg-white" />
                  <motion.span variants={{ closed:{ opacity:1 }, open:{ opacity:0 } }}
                    className="absolute left-0 top-1/2 w-6 h-[2.5px] rounded-full bg-white" />
                  <motion.span variants={{ closed:{ rotate:0, y:7 }, open:{ rotate:-45, y:0 } }}
                    className="absolute left-0 top-1/2 w-6 h-[2.5px] rounded-full bg-white" />
                </motion.div>
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* ── MOBILE MENU — full screen, top to bottom ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9998] flex flex-col bg-[#f2f7ec]"
          >
            {/* ── TOP BAR ── */}
            <div className="flex items-center justify-between px-5 pt-4 pb-3 bg-[#1f4d2b] shrink-0">
              {/* rainbow stripe at very top */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px]"
                style={{ background:'repeating-linear-gradient(90deg,#c8340a 0,#c8340a 32px,#d89c10 32px,#d89c10 64px,#7fcf65 64px,#7fcf65 96px)' }}
              />

              {/* logo */}
              <div className="flex items-center gap-3 mt-1">
                <div className="rounded-xl overflow-hidden border-2 border-white/20 shrink-0">
                  <img src="/images/logo.JPG" alt="logo" className="h-[40px] w-auto object-cover" />
                </div>
                <div>
                  <p className="text-white font-black text-base leading-none">
                    {lang === 'ja' ? '掛川バギーパーク' : 'Kakegawa Buggy'}
                  </p>
                  <p className="text-white/55 text-[10px] font-bold tracking-[0.18em] uppercase mt-0.5">
                    Outdoor Adventure
                  </p>
                </div>
              </div>

              {/* right: lang + close */}
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={toggleLang}
                  className="flex items-center gap-1.5 h-[38px] px-4 rounded-xl bg-white/15 text-white font-black text-xs border border-white/20 hover:bg-white/25 transition-all"
                >
                  <FiGlobe size={13} />
                  {lang === 'ja' ? 'EN' : 'JP'}
                </button>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-[38px] h-[38px] rounded-xl bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-all border border-white/20"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            {/* ── SCROLLABLE CONTENT ── */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 pt-6 pb-6">

                {/* section label */}
                <p className="text-[10px] font-extrabold tracking-[0.3em] uppercase text-[#1f4d2b]/40 mb-4">
                  {lang === 'ja' ? 'メニュー' : 'Navigation'}
                </p>

                {/* nav links */}
                <div className="flex flex-col gap-3 mb-8">
                  {NAV_LINKS.map((ln, i) => (
                    <MobileLink
                      key={ln.key}
                      label={t(`nav.${ln.key}`)}
                      num={ln.num}
                      delay={i * 0.06}
                      onClick={() => handleNav(ln.id)}
                    />
                  ))}
                </div>

                {/* divider */}
                <div className="h-px bg-[#1f4d2b]/10 mb-6" />

                {/* CTA section */}
                <p className="text-[10px] font-extrabold tracking-[0.3em] uppercase text-[#1f4d2b]/40 mb-4">
                  {lang === 'ja' ? 'ご予約' : 'Booking'}
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={openBooking}
                    className="w-full h-[64px] rounded-[20px] bg-[#1f4d2b] text-white text-lg font-black shadow-lg flex items-center justify-center gap-3 active:scale-[.98] transition-transform"
                  >
                    <FiCalendar size={20} />
                    {lang === 'ja' ? '今すぐ予約する' : 'Reserve Now'}
                  </button>

                  <a
                    href="tel:0537269489"
                    className="w-full h-[60px] rounded-[20px] bg-white border-2 border-[#e8f0e0] text-[#173617] text-lg font-black flex items-center justify-center gap-3 shadow-sm no-underline active:scale-[.98] transition-transform"
                  >
                    <FiPhone size={20} />
                    0537-26-9489
                  </a>
                </div>

              </div>
            </div>

            {/* ── BOTTOM SAFE AREA ── */}
            <div className="h-[env(safe-area-inset-bottom,16px)] bg-[#f2f7ec] shrink-0" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FLOATING BOOK BUTTON ── */}
      <motion.button
        onClick={openBooking}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.96 }}
        className="fixed bottom-5 right-5 z-[997] h-[58px] px-6 rounded-full bg-[#1f4d2b] text-white font-black shadow-[0_10px_28px_rgba(31,77,43,.4)] flex items-center gap-3"
      >
        <FiCalendar size={17} />
        <span className="text-sm">{lang === 'ja' ? '予約' : 'Book'}</span>
      </motion.button>

      {/* ── BOOKING POPUP ── */}
      <BookingPopup open={popupOpen} onClose={() => setPopupOpen(false)} />
    </>
  );
}